// src/pages/LaserCO2CalculatorPage.tsx
import React, { useState } from 'react';
import { 
  Zap, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Gauge,
  Shield,
  FileText,
  Download,
  Cpu,
  Wind,
  Target,
  DollarSign,
  Info,
  Copy
} from 'lucide-react';
import { 
  calculateLaserParameters, 
  generateGCode, 
  estimateCuttingTime,
  validateLaserSetup,
  type LaserParameters 
} from '../utils/laserCO2Calculator';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { convertPolishChars } from '../utils/pdfFonts';

const LaserCO2CalculatorPage: React.FC = () => {
  const [parameters, setParameters] = useState<LaserParameters>({
    material: 'PMMA',
    thickness: 3,
    laserPower: 100,
    operation: 'cut',
    quality: 'standard',
    focalLength: 2.5,
  });

  const [result, setResult] = useState<any>(null);
  const [pathLength, setPathLength] = useState(1000); // mm
  const [timeEstimate, setTimeEstimate] = useState<any>(null);
  const [validation, setValidation] = useState<any>(null);
  const [showGCode, setShowGCode] = useState(false);
  const [gcode, setGcode] = useState('');

  const handleCalculate = () => {
    try {
      // Walidacja
      const validationResult = validateLaserSetup(parameters);
      setValidation(validationResult);
      
      // Kalkulacja
      const calcResult = calculateLaserParameters(parameters);
      setResult(calcResult);
      
      // Estymacja czasu
      const estimate = estimateCuttingTime(pathLength, parameters, calcResult);
      setTimeEstimate(estimate);
      
      // Generuj przykładowy G-code
      const generatedGCode = generateGCode(
        parameters, 
        calcResult, 
        'rectangle', 
        { width: 100, height: 100 }
      );
      setGcode(generatedGCode);
    } catch (error: any) {
      console.error('Błąd kalkulacji:', error);
      alert('Wystąpił błąd podczas kalkulacji: ' + error.message);
    }
  };

  const generatePDF = () => {
    if (!result) return;

    const pdf = new jsPDF() as any; // Type assertion for autoTable
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    
    // Kolory
    const primaryColor = [249, 115, 22]; // orange-500
    const darkBg = [39, 39, 42]; // zinc-800
    const lightText = [161, 161, 170]; // zinc-400
    const dangerColor = [239, 68, 68]; // red-500
    const warningColor = [245, 158, 11]; // amber-500
    const successColor = [34, 197, 94]; // green-500
    
    // Funkcja pomocnicza do rysowania tła gradientowego
    const drawGradientBackground = (y: number, height: number) => {
      const steps = 20;
      for (let i = 0; i < steps; i++) {
        const ratio = i / steps;
        const color = [
          249 - ratio * 20,
          115 - ratio * 10,
          22 + ratio * 10
        ];
        pdf.setFillColor(color[0], color[1], color[2]);
        pdf.rect(0, y + (height / steps) * i, pageWidth, height / steps, 'F');
      }
    };
    
    // STRONA 1 - Nagłówek i parametry
    // Nagłówek z gradientem
    drawGradientBackground(0, 45);
    
    // Logo placeholder
    pdf.setFillColor(255, 255, 255);
    pdf.circle(20, 22, 8, 'F');
    pdf.setTextColor(249, 115, 22);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('CO2', 20, 25, { align: 'center' });
    
    // Tytuł
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(24);
    pdf.text(convertPolishChars('Parametry Cięcia Laserem CO2'), pageWidth / 2, 20, { align: 'center' });
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(convertPolishChars('Raport techniczny obróbki laserowej'), pageWidth / 2, 28, { align: 'center' });
    
    // Data i numer dokumentu
    pdf.setFontSize(10);
    pdf.text(`Data: ${new Date().toLocaleDateString('pl-PL')}`, pageWidth - 20, 15, { align: 'right' });
    pdf.text(`Dokument: #${Date.now().toString().slice(-8)}`, pageWidth - 20, 20, { align: 'right' });
    
    let y = 55;
    
    // Sekcja z materiałem - stylizowana
    pdf.setFillColor(245, 245, 247);
    pdf.roundedRect(15, y, pageWidth - 30, 35, 3, 3, 'F');
    
    // Ikona materiału
    const materialColors: { [key: string]: number[] } = {
      PMMA: [59, 130, 246], // blue
      PC: [147, 51, 234], // purple
      PET: [34, 197, 94], // green
      PS: [234, 179, 8], // yellow
      Wood: [180, 83, 9], // amber
      MDF: [234, 88, 12], // orange
      Plywood: [202, 138, 4], // yellow-dark
      Paper: [156, 163, 175], // gray
      Fabric: [236, 72, 153], // pink
      Leather: [146, 64, 14], // brown
      Rubber: [31, 41, 55], // gray-dark
    };
    
    const matColor = materialColors[parameters.material] || [100, 100, 100];
    pdf.setFillColor(matColor[0], matColor[1], matColor[2]);
    pdf.circle(30, y + 17, 5, 'F');
    
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.text(convertPolishChars(`Materiał: ${parameters.material}`), 45, y + 15);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    pdf.text(convertPolishChars(`Grubość: ${parameters.thickness} mm`), 45, y + 22);
    
    // Operacja
    const operationNames: { [key: string]: string } = {
      cut: 'Ciecie',
      engrave: 'Grawerowanie',
      mark: 'Znakowanie'
    };
    pdf.text(convertPolishChars(`Operacja: ${operationNames[parameters.operation]}`), 120, y + 15);
    const qualityNames: { [key: string]: string } = {
      draft: 'Szkic',
      standard: 'Standard',
      high: 'Wysoka'
    };
    pdf.text(convertPolishChars(`Jakość: ${qualityNames[parameters.quality]}`), 120, y + 22);
    
    y += 45;
    
    // Tabela z parametrami maszynowymi
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.setTextColor(39, 39, 42);
    pdf.text(convertPolishChars('PARAMETRY MASZYNOWE'), 15, y);
    y += 8;
    
    // Rysuj tabelę parametrów
    const tableData = [
      [convertPolishChars('Parametr'), convertPolishChars('Wartość'), convertPolishChars('Jednostka')],
      [convertPolishChars('Moc lasera'), parameters.laserPower.toString(), 'W'],
      [convertPolishChars('Soczewka'), `${parameters.focalLength}"`, 'cal'],
      [convertPolishChars('Długość ścieżki'), pathLength.toString(), 'mm'],
    ];
    
    // Autoformatowanie tabeli
    pdf.autoTable({
      startY: y,
      head: [tableData[0]],
      body: tableData.slice(1),
      theme: 'grid',
      headStyles: {
        fillColor: [249, 115, 22],
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 10,
        cellPadding: 3
      },
      alternateRowStyles: {
        fillColor: [250, 250, 251]
      },
      margin: { left: 15, right: 15 }
    });
    
    y = pdf.lastAutoTable.finalY + 15;
    
    // Wyniki obliczeń - główne parametry w kartach
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text(convertPolishChars('PARAMETRY OBRÓBKI'), 15, y);
    y += 10;
    
    // Karty z parametrami
    const cardWidth = (pageWidth - 45) / 4;
    const cardHeight = 25;
    const cards = [
      { label: convertPolishChars('Prędkość'), value: `${result.speed}`, unit: 'mm/s', color: primaryColor },
      { label: 'Moc', value: `${result.power}`, unit: '%', color: dangerColor },
      { label: convertPolishChars('Częstotliwość'), value: `${result.frequency}`, unit: 'Hz', color: [147, 51, 234] },
      { label: convertPolishChars('Przejścia'), value: `${result.passes}`, unit: 'x', color: [59, 130, 246] }
    ];
    
    cards.forEach((card, index) => {
      const x = 15 + index * (cardWidth + 3);
      
      // Tło karty
      pdf.setFillColor(250, 250, 251);
      pdf.roundedRect(x, y, cardWidth, cardHeight, 2, 2, 'F');
      
      // Kolorowy akcent na górze
      pdf.setFillColor(card.color[0], card.color[1], card.color[2]);
      pdf.rect(x, y, cardWidth, 3, 'F');
      
      // Tekst
      pdf.setTextColor(100, 100, 100);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text(card.label, x + cardWidth/2, y + 8, { align: 'center' });
      
      pdf.setTextColor(card.color[0], card.color[1], card.color[2]);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text(card.value, x + cardWidth/2, y + 16, { align: 'center' });
      
      pdf.setTextColor(100, 100, 100);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text(card.unit, x + cardWidth/2, y + 22, { align: 'center' });
    });
    
    y += cardHeight + 15;
    
    // Szczegółowe parametry w tabeli
    const detailsData = [
      [convertPolishChars('Parametr'), convertPolishChars('Wartość')],
      [convertPolishChars('Wydmuch powietrza'), result.airAssist],
      [convertPolishChars('Pozycja ogniska'), `${result.focalPosition} mm`],
      [convertPolishChars('Szerokość cięcia (kerf)'), `${result.kerf} mm`],
      [convertPolishChars('Prędkość w narożnikach'), `${result.minCornerSpeed} mm/s`],
    ];
    
    pdf.autoTable({
      startY: y,
      head: [detailsData[0]],
      body: detailsData.slice(1),
      theme: 'striped',
      headStyles: {
        fillColor: [39, 39, 42],
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 10
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 'auto', fontStyle: 'bold' }
      },
      margin: { left: 15, right: 15 }
    });
    
    y = pdf.lastAutoTable.finalY + 15;
    
    // Estymacja kosztów
    if (timeEstimate && y < 200) {
      pdf.setFillColor(236, 253, 245);
      pdf.roundedRect(15, y, pageWidth - 30, 30, 3, 3, 'F');
      
      pdf.setTextColor(34, 197, 94);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.text(convertPolishChars('ESTYMACJA'), 25, y + 10);
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      const timeMinutes = Math.floor(timeEstimate.time / 60);
      const timeSeconds = timeEstimate.time % 60;
      pdf.text(convertPolishChars(`Czas obróbki (${(pathLength/1000).toFixed(1)}m): ${timeMinutes} min ${timeSeconds} s`), 25, y + 18);
      pdf.text(convertPolishChars(`Koszt: ${timeEstimate.cost} PLN`), 25, y + 24);
      pdf.text(convertPolishChars(`Zużycie energii: ${result.energyUsage} kWh/m`), 120, y + 18);
    }
    
    // STRONA 2 - Ostrzeżenia, wskazówki, BHP
    pdf.addPage();
    y = 20;
    
    // Nagłówek strony 2
    pdf.setFillColor(39, 39, 42);
    pdf.rect(0, 0, pageWidth, 30, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text(convertPolishChars('Informacje Techniczne i Bezpieczeństwo'), pageWidth / 2, 20, { align: 'center' });
    
    y = 40;
    
    // Ostrzeżenia
    if (result.warnings && result.warnings.length > 0) {
      pdf.setFillColor(254, 242, 242);
      pdf.roundedRect(15, y, pageWidth - 30, 10 + result.warnings.length * 7, 3, 3, 'F');
      
      pdf.setTextColor(239, 68, 68);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text(convertPolishChars('OSTRZEŻENIA'), 20, y + 7);
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      let warningY = y + 14;
      result.warnings.forEach((warning: string) => {
        const lines = pdf.splitTextToSize(convertPolishChars(warning), pageWidth - 50);
        lines.forEach((line: string) => {
          pdf.text(`- ${line}`, 25, warningY);
          warningY += 5;
        });
      });
      y = warningY + 5;
    }
    
    // Wskazówki
    if (result.tips && result.tips.length > 0) {
      pdf.setFillColor(239, 246, 255);
      pdf.roundedRect(15, y, pageWidth - 30, 10 + result.tips.length * 7, 3, 3, 'F');
      
      pdf.setTextColor(59, 130, 246);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text(convertPolishChars('WSKAZÓWKI'), 20, y + 7);
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      let tipY = y + 14;
      result.tips.forEach((tip: string) => {
        const lines = pdf.splitTextToSize(convertPolishChars(tip), pageWidth - 50);
        lines.forEach((line: string) => {
          pdf.text(`- ${line}`, 25, tipY);
          tipY += 5;
        });
      });
      y = tipY + 5;
    }
    
    // BHP
    pdf.setFillColor(255, 251, 235);
    pdf.roundedRect(15, y, pageWidth - 30, 10 + result.safetyNotes.length * 7, 3, 3, 'F');
    
    pdf.setTextColor(245, 158, 11);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text(convertPolishChars('ZASADY BHP'), 20, y + 7);
    
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    let safetyY = y + 14;
    result.safetyNotes.forEach((note: string) => {
      pdf.text(convertPolishChars(`- ${note}`), 25, safetyY);
      safetyY += 6;
    });
    
    // G-Code przykład (jeśli się zmieści)
    if (gcode && safetyY < 220) {
      y = safetyY + 10;
      
      pdf.setFillColor(243, 244, 246);
      pdf.roundedRect(15, y, pageWidth - 30, 45, 3, 3, 'F');
      
      pdf.setTextColor(75, 85, 99);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text(convertPolishChars('PRZYKŁADOWY G-CODE'), 20, y + 7);
      
      pdf.setFont('courier', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(31, 41, 55);
      const codeLines = gcode.split('\n').slice(0, 8);
      let codeY = y + 14;
      codeLines.forEach((line: string) => {
        pdf.text(line, 20, codeY);
        codeY += 4;
      });
    }
    
    // Stopka na każdej stronie
    const addFooter = (pageNum: number) => {
      pdf.setDrawColor(200, 200, 200);
      pdf.line(15, pageHeight - 20, pageWidth - 15, pageHeight - 20);
      
      pdf.setFontSize(9);
      pdf.setTextColor(128, 128, 128);
      pdf.setFont('helvetica', 'normal');
      
      // Logo i nazwa firmy
      pdf.text('PlexiSystem CRM', 15, pageHeight - 12);
      pdf.text('(c) 2025 PlexiSystem', 15, pageHeight - 8);
      
      // Data i godzina
      const now = new Date();
      pdf.text(
        `Wygenerowano: ${now.toLocaleDateString('pl-PL')} ${now.toLocaleTimeString('pl-PL')}`,
        pageWidth / 2,
        pageHeight - 12,
        { align: 'center' }
      );
      
      // Numer strony
      pdf.text(
        `Strona ${pageNum} z 2`,
        pageWidth - 15,
        pageHeight - 12,
        { align: 'right' }
      );
      
      // Kontakt
      pdf.setFontSize(8);
      pdf.text('www.plexisystem.pl', pageWidth - 15, pageHeight - 8, { align: 'right' });
    };
    
    // Dodaj stopki
    pdf.setPage(1);
    addFooter(1);
    pdf.setPage(2);
    addFooter(2);
    
    // Zapisz PDF
    const filename = `laser_CO2_${parameters.material}_${parameters.thickness}mm_${new Date().toISOString().slice(0, 10)}.pdf`;
    pdf.save(filename);
  };

  const copyGCode = () => {
    navigator.clipboard.writeText(gcode);
    alert('G-Code skopiowany do schowka!');
  };

  // Kolory dla różnych materiałów
  const getMaterialColor = (material: string) => {
    const colors: { [key: string]: string } = {
      PMMA: 'bg-blue-500',
      PC: 'bg-purple-500',
      PET: 'bg-green-500',
      PS: 'bg-yellow-500',
      Wood: 'bg-amber-700',
      MDF: 'bg-orange-700',
      Plywood: 'bg-yellow-700',
      Paper: 'bg-gray-300',
      Fabric: 'bg-pink-500',
      Leather: 'bg-amber-800',
      Rubber: 'bg-gray-800',
    };
    return colors[material] || 'bg-gray-500';
  };

  return (
    <div className="min-h-screen bg-zinc-900 text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Nagłówek */}
        <div className="bg-gradient-to-r from-orange-600 to-orange-500 rounded-xl p-8 mb-8">
          <div className="flex items-center gap-4">
            <Zap className="w-12 h-12" />
            <div>
              <h1 className="text-3xl font-bold">Kalkulator Parametrów Lasera CO2</h1>
              <p className="text-orange-100 mt-2">
                Optymalne parametry cięcia i grawerowania dla różnych materiałów
              </p>
            </div>
          </div>
        </div>

        {/* Alert o toksycznych materiałach */}
        {(parameters.material === 'PC' || parameters.material === 'Rubber' || 
          (parameters.material === 'MDF' && parameters.thickness > 6)) && (
          <div className="bg-red-900/50 border border-red-500 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-red-500">UWAGA - Materiał może uwalniać toksyczne opary!</h3>
              <p className="text-red-200 mt-1">
                {parameters.material === 'PC' && 'Poliwęglan uwalnia toksyczne opary przy cięciu laserem. Wymagana doskonała wentylacja i filtracja!'}
                {parameters.material === 'Rubber' && 'Guma może uwalniać toksyczne gazy. Zapewnij bardzo dobrą wentylację!'}
                {parameters.material === 'MDF' && 'MDF może uwalniać formaldehyd. Używaj dobrej wentylacji!'}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Panel parametrów */}
          <div className="bg-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Cpu className="w-6 h-6 text-orange-500" />
              Parametry Zadania
            </h2>

            <div className="space-y-4">
              {/* Materiał */}
              <div>
                <label className="block text-sm font-medium mb-2">Materiał</label>
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${getMaterialColor(parameters.material)}`} />
                  <select
                    value={parameters.material}
                    onChange={(e) => setParameters({ ...parameters, material: e.target.value as any })}
                    className="flex-1 bg-zinc-700 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
                  >
                    <optgroup label="Tworzywa sztuczne">
                      <option value="PMMA">PMMA (Pleksi)</option>
                      <option value="PC">PC (Poliwęglan) ⚠️</option>
                      <option value="PET">PET</option>
                      <option value="PS">PS (Polistyren)</option>
                    </optgroup>
                    <optgroup label="Drewno i pochodne">
                      <option value="Wood">Drewno</option>
                      <option value="MDF">MDF</option>
                      <option value="Plywood">Sklejka</option>
                    </optgroup>
                    <optgroup label="Inne materiały">
                      <option value="Paper">Papier</option>
                      <option value="Fabric">Tkanina</option>
                      <option value="Leather">Skóra</option>
                      <option value="Rubber">Guma ⚠️</option>
                    </optgroup>
                  </select>
                </div>
              </div>

              {/* Grubość */}
              <div>
                <label className="block text-sm font-medium mb-2">Grubość materiału [mm]</label>
                <input
                  type="number"
                  value={parameters.thickness}
                  onChange={(e) => setParameters({ ...parameters, thickness: Number(e.target.value) })}
                  className="w-full bg-zinc-700 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
                  min="0.1"
                  max="30"
                  step="0.5"
                />
              </div>

              {/* Moc lasera */}
              <div>
                <label className="block text-sm font-medium mb-2">Moc lasera [W]</label>
                <select
                  value={parameters.laserPower}
                  onChange={(e) => setParameters({ ...parameters, laserPower: Number(e.target.value) })}
                  className="w-full bg-zinc-700 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
                >
                  <option value="40">40W</option>
                  <option value="60">60W</option>
                  <option value="80">80W</option>
                  <option value="100">100W</option>
                  <option value="130">130W</option>
                  <option value="150">150W</option>
                  <option value="180">180W</option>
                </select>
              </div>

              {/* Operacja */}
              <div>
                <label className="block text-sm font-medium mb-2">Typ operacji</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setParameters({ ...parameters, operation: 'cut' })}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      parameters.operation === 'cut' 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                    }`}
                  >
                    Cięcie
                  </button>
                  <button
                    onClick={() => setParameters({ ...parameters, operation: 'engrave' })}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      parameters.operation === 'engrave' 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                    }`}
                  >
                    Grawerowanie
                  </button>
                  <button
                    onClick={() => setParameters({ ...parameters, operation: 'mark' })}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      parameters.operation === 'mark' 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                    }`}
                  >
                    Znakowanie
                  </button>
                </div>
              </div>

              {/* Jakość */}
              <div>
                <label className="block text-sm font-medium mb-2">Jakość</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setParameters({ ...parameters, quality: 'draft' })}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      parameters.quality === 'draft' 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                    }`}
                  >
                    Szkic
                  </button>
                  <button
                    onClick={() => setParameters({ ...parameters, quality: 'standard' })}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      parameters.quality === 'standard' 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                    }`}
                  >
                    Standard
                  </button>
                  <button
                    onClick={() => setParameters({ ...parameters, quality: 'high' })}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      parameters.quality === 'high' 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                    }`}
                  >
                    Wysoka
                  </button>
                </div>
              </div>

              {/* Soczewka */}
              <div>
                <label className="block text-sm font-medium mb-2">Ogniskowa soczewki</label>
                <select
                  value={parameters.focalLength}
                  onChange={(e) => setParameters({ ...parameters, focalLength: Number(e.target.value) })}
                  className="w-full bg-zinc-700 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
                >
                  <option value="1.5">1.5" (do 3mm)</option>
                  <option value="2">2" (do 6mm)</option>
                  <option value="2.5">2.5" (do 10mm)</option>
                  <option value="3">3" (do 15mm)</option>
                  <option value="4">4" (do 20mm)</option>
                  <option value="5">5" (do 30mm)</option>
                </select>
              </div>

              {/* Długość ścieżki */}
              <div>
                <label className="block text-sm font-medium mb-2">Długość ścieżki cięcia [mm]</label>
                <input
                  type="number"
                  value={pathLength}
                  onChange={(e) => setPathLength(Number(e.target.value))}
                  className="w-full bg-zinc-700 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
                  min="10"
                  max="100000"
                  step="100"
                />
              </div>

              {/* Przycisk kalkulacji */}
              <button
                onClick={handleCalculate}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-6 py-3 font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Gauge className="w-5 h-5" />
                Oblicz parametry
              </button>
            </div>
          </div>

          {/* Panel wyników */}
          <div className="space-y-6">
            {result && (
              <>
                {/* Walidacja */}
                {validation && !validation.valid && (
                  <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-xl p-4">
                    <h3 className="font-bold text-yellow-400 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Problemy z konfiguracją
                    </h3>
                    <ul className="space-y-1 text-sm">
                      {validation.errors.map((error: string, idx: number) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-yellow-500 mt-1">!</span>
                          <span>{error}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Główne parametry */}
                <div className="bg-zinc-800 rounded-xl p-6">
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    Parametry Obróbki
                  </h2>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-zinc-700 rounded-lg p-4">
                      <div className="text-sm text-zinc-400 mb-1">Prędkość</div>
                      <div className="text-2xl font-bold text-orange-500">{result.speed} mm/s</div>
                      <div className="text-xs text-zinc-500">{(result.speed * 60).toFixed(0)} mm/min</div>
                    </div>
                    
                    <div className="bg-zinc-700 rounded-lg p-4">
                      <div className="text-sm text-zinc-400 mb-1">Moc</div>
                      <div className="text-2xl font-bold text-red-500">{result.power}%</div>
                      <div className="text-xs text-zinc-500">{(parameters.laserPower * result.power / 100).toFixed(1)}W</div>
                    </div>
                    
                    <div className="bg-zinc-700 rounded-lg p-4">
                      <div className="text-sm text-zinc-400 mb-1">Częstotliwość</div>
                      <div className="text-2xl font-bold text-purple-500">{result.frequency} Hz</div>
                    </div>
                    
                    <div className="bg-zinc-700 rounded-lg p-4">
                      <div className="text-sm text-zinc-400 mb-1">Przejścia</div>
                      <div className="text-2xl font-bold text-blue-500">{result.passes}x</div>
                    </div>
                  </div>

                  <div className="mt-4 space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Wind className="w-4 h-4 text-cyan-500" />
                        Wydmuch powietrza
                      </span>
                      <span className="font-medium">{result.airAssist}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-green-500" />
                        Pozycja ogniska
                      </span>
                      <span className="font-medium">{result.focalPosition} mm</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Cpu className="w-4 h-4 text-yellow-500" />
                        Szerokość cięcia (kerf)
                      </span>
                      <span className="font-medium">{result.kerf} mm</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Gauge className="w-4 h-4 text-orange-500" />
                        Prędkość w narożnikach
                      </span>
                      <span className="font-medium">{result.minCornerSpeed} mm/s</span>
                    </div>
                  </div>
                </div>

                {/* Estymacja czasu i kosztów */}
                {timeEstimate && (
                  <div className="bg-zinc-800 rounded-xl p-6">
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-blue-500" />
                      Estymacja dla {pathLength}mm ścieżki
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-zinc-400 mb-1">Czas obróbki</div>
                        <div className="text-xl font-bold">
                          {Math.floor(timeEstimate.time / 60)}:{(timeEstimate.time % 60).toString().padStart(2, '0')}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-zinc-400 mb-1">Koszt</div>
                        <div className="text-xl font-bold text-green-500">{timeEstimate.cost} PLN</div>
                      </div>
                    </div>
                    
                    <div className="mt-3 text-sm text-zinc-400">
                      Zużycie energii: {result.energyUsage} kWh/m
                    </div>
                  </div>
                )}

                {/* Ostrzeżenia */}
                {result.warnings && result.warnings.length > 0 && (
                  <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-6">
                    <h3 className="font-bold mb-4 flex items-center gap-2 text-red-400">
                      <AlertTriangle className="w-5 h-5" />
                      Ostrzeżenia
                    </h3>
                    <ul className="space-y-2">
                      {result.warnings.map((warning: string, idx: number) => (
                        <li key={idx} className="text-sm flex items-start gap-2">
                          <span className="text-red-500 mt-1">!</span>
                          <span>{warning}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Wskazówki */}
                {result.tips && result.tips.length > 0 && (
                  <div className="bg-zinc-800 rounded-xl p-6">
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Info className="w-5 h-5 text-blue-500" />
                      Wskazówki
                    </h3>
                    <ul className="space-y-2">
                      {result.tips.map((tip: string, idx: number) => (
                        <li key={idx} className="text-sm flex items-start gap-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5" />
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Uwagi o jakości */}
                {result.qualityNotes && result.qualityNotes.length > 0 && (
                  <div className="bg-zinc-800 rounded-xl p-6">
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      Jakość obróbki
                    </h3>
                    <ul className="space-y-2">
                      {result.qualityNotes.map((note: string, idx: number) => (
                        <li key={idx} className="text-sm flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full" />
                          <span>{note}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* BHP */}
                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-6">
                  <h3 className="font-bold mb-4 flex items-center gap-2 text-yellow-400">
                    <Shield className="w-5 h-5" />
                    Zasady BHP
                  </h3>
                  <ul className="space-y-2">
                    {result.safetyNotes.map((note: string, idx: number) => (
                      <li key={idx} className="text-sm flex items-start gap-2">
                        <span className="text-yellow-500 mt-1">⚠</span>
                        <span>{note}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* G-Code */}
                <div className="bg-zinc-800 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold flex items-center gap-2">
                      <FileText className="w-5 h-5 text-purple-500" />
                      G-Code
                    </h3>
                    <button
                      onClick={() => setShowGCode(!showGCode)}
                      className="text-sm text-purple-400 hover:text-purple-300"
                    >
                      {showGCode ? 'Ukryj' : 'Pokaż'} kod
                    </button>
                  </div>
                  
                  {showGCode && (
                    <>
                      <div className="bg-zinc-900 rounded-lg p-4 font-mono text-xs text-green-400 max-h-64 overflow-y-auto">
                        <pre>{gcode}</pre>
                      </div>
                      <button
                        onClick={copyGCode}
                        className="mt-4 w-full bg-purple-600 hover:bg-purple-700 text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        <Copy className="w-4 h-4" />
                        Kopiuj G-Code
                      </button>
                    </>
                  )}
                </div>

                {/* Przyciski akcji */}
                <div className="flex gap-4">
                  <button
                    onClick={generatePDF}
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-6 py-3 font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Pobierz PDF
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LaserCO2CalculatorPage;